import resolveResponse from 'contentful-resolve-response'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {toPayload} from '../../../../lib/types/payload'
import {toBrandPage} from '../../../../brand/lib/types/contentful'

import {ThemeProvider} from '@primer/react-brand'

// Custom components
import {Highlight} from './_components/Highlight/Highlight'
import {River} from './_components/River/River'
import {RiverAccordion} from './_components/RiverAccordion/RiverAccordion'
import {Hero} from './_components/Hero/Hero'
import {Overview} from './_components/Overview/Overview'
import {Media} from './_components/Media/Media'

import Styles from './Index.module.css'

export default function EventsUniverseRecapIndex() {
  const {contentfulRawJsonResponse} = toPayload(useRoutePayload<unknown>())
  const contentfulResponse = resolveResponse(contentfulRawJsonResponse)
  const page = toBrandPage(contentfulResponse)
  const sections = page.fields.template.fields.content || []

  return (
    <ThemeProvider className={Styles.Page} colorMode="light">
      {sections.map((section, sectionIndex) => {
        //
        // Section
        //
        const sectionId = section.fields.htmlId || '' // Used for deeplinking
        const location = sectionId ? `section_${sectionId}` : `section_${sectionIndex + 1}` // Used for analytics
        const theme = section.fields.content?.at(0)?.fields.label?.fields.color || 'green' // Used for setting a theme color
        const components = section.fields.content || []

        return (
          <section
            key={section.sys?.id || sectionIndex}
            className={Styles.Section}
            {...(sectionId && {id: sectionId})}
            {...(theme && {'data-theme': theme})}
          >
            <div className={Styles.Container}>
              {components.map((component, contentIndex) => {
                //
                // Component
                //
                const contentType = component?.sys.contentType.sys.id
                const customComponent = component?.fields.id || '' // ID is used for choosing custom component

                return (
                  <div
                    key={component?.sys?.id || contentIndex}
                    className={Styles.Component}
                    {...(sectionIndex === 0 && contentIndex === 0 && {'data-hpc': true})} // Mark first component in first section with data-hpc for performance tracking
                    {...(customComponent === 'River' && {'data-spacing': 'top'})} // Add spacing only for River components
                  >
                    {
                      // Choose custom component based on ID and content type
                      customComponent === 'Hero' && contentType === 'genericContent' ? (
                        // Hero
                        <Hero
                          content={component}
                          location={location}
                          sectionId={sectionId}
                          sectionIndex={sectionIndex}
                        />
                      ) : customComponent === 'Overview' && contentType === 'genericContent' ? (
                        // Overview
                        <Overview content={component} location={location} index={contentIndex} />
                      ) : customComponent === 'River' && contentType === 'genericContent' ? (
                        // River
                        <River content={component} location={location} index={contentIndex} />
                      ) : customComponent === 'RiverAccordion' && contentType === 'genericContent' ? (
                        // RiverAccordion
                        <RiverAccordion content={component} location={location} index={contentIndex} />
                      ) : customComponent === 'Highlight' && contentType === 'genericContent' ? (
                        // Highlight
                        <Highlight content={component} location={location} />
                      ) : customComponent === 'Media' && contentType === 'genericContent' ? (
                        // Media
                        // Load eagerly only for media in first section
                        <Media content={component} location={location} loading={sectionIndex > 0 ? 'lazy' : 'eager'} />
                      ) : null
                    }
                  </div>
                )
              })}
            </div>
          </section>
        )
      })}
    </ThemeProvider>
  )
}

try{ EventsUniverseRecapIndex.displayName ||= 'EventsUniverseRecapIndex' } catch {}